import { TextField, type TextFieldProps } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { type Control, type FieldValues, type Path, useController } from 'react-hook-form';

interface FormTextFieldProps<T extends FieldValues>
  extends Omit<TextFieldProps, 'errorMessage' | 'isInvalid' | 'onChange' | 'value'> {
  /**
   * The control object from the `useForm` hook.
   */
  control: Control<T>;
  /**
   * The name of the field.
   */
  name: Path<T>;
}

function FormTextField<T extends FieldValues>(props: FormTextFieldProps<T>, ref?: React.Ref<HTMLDivElement>) {
  const { name, control, isDisabled, className, ...other } = props;

  const { field, fieldState, formState } = useController({ name, control });

  const defaultValue = React.useMemo(
    () => (formState?.defaultValues ? formState?.defaultValues[name] : undefined),
    [formState?.defaultValues, name],
  );

  return (
    <TextField
      className={cx('min-h-[96px]', className)}
      defaultValue={defaultValue}
      errorMessage={fieldState?.error?.message}
      inputRef={field?.ref}
      isDisabled={formState.isSubmitting || isDisabled}
      isInvalid={fieldState?.invalid}
      name={name}
      value={field?.value}
      onChange={field?.onChange}
      ref={ref}
      {...other}
    />
  );
}

export { FormTextField };
export type { FormTextFieldProps };
